import type { TeamRequest, TeamRequestAction } from "../../api/teams-api.js";

/**
 * WHICH DIRECTION A PENDING REQUEST POINTS, and therefore what the reader may
 * do about it (ADR-0082).
 *
 * THIS EXISTS BECAUSE BOTH SURFACES GOT IT WRONG THE SAME WAY. `/requests/incoming`
 * is scoped by OWNERSHIP, not by kind: `listRequestsForOwner` returns every
 * pending row on a team the caller owns, which is a join request somebody sent
 * them AND an invitation they themselves sent out. Both screens treated the
 * whole list as join requests, so an owner who invited somebody was shown
 * "<the invitee> asked to join" with an **Approve** button. The server refused
 * it — `assertMayResolve` answers "Only the invited person can answer this
 * invitation" — so nothing was ever wrongly granted, but the screen described
 * the opposite of what had happened and offered a button that could not work.
 *
 * THE DIRECTION IS FULLY DETERMINED BY (queue, kind), with no identity lookup:
 *
 * | queue      | kind         | the reader is | they can          |
 * | ---------- | ------------ | ------------- | ----------------- |
 * | `mine`     | invitation   | the invitee   | accept · decline  |
 * | `mine`     | join_request | the asker     | withdraw          |
 * | `incoming` | join_request | an owner      | approve · decline |
 * | `incoming` | invitation   | the sender    | withdraw          |
 *
 * `mine` is `listRequestsForSubject`, so the reader IS the subject by
 * construction. `incoming` is owner-scoped, and the reader can never be the
 * subject there — an owner is already a member, so nobody invites them and they
 * do not ask to join. That is what makes the queue enough.
 *
 * IT MIRRORS `assertMayResolve` EXACTLY AND RELAXES NOTHING. Every action
 * offered here is one the server's authority table permits for that reader;
 * every action it withholds is one the server refuses. A widened button would
 * still be refused — the point is not to gate, it is to stop describing an
 * outgoing invitation as an incoming request.
 */
export type RequestQueue = "mine" | "incoming";

/** `inbound` is somebody else's proposal that the reader DECIDES; `outbound` is
 *  the reader's own, which they can only take back. The two belong under
 *  different headings, because Approve and Withdraw in one column under one
 *  heading mean opposite things. */
export type RequestDirection = "inbound" | "outbound";

/** A row action is never accented (DS invariant §9): the one that answers
 *  the proposal is `secondary` (the Button default), the one that turns it
 *  down or takes it back is `ghost`. */
export interface RequestAction {
  readonly action: TeamRequestAction;
  readonly label: string;
  readonly variant?: "ghost";
}

export interface RequestPresentation {
  readonly direction: RequestDirection;
  /** One sentence naming WHO did what, in the reader's own terms. */
  readonly headline: string;
  /** Who the row is ABOUT, for its leading mark: the other person, or the team
   *  itself when the only person involved is the reader ("You asked to join"). */
  readonly subject:
    | {
        readonly kind: "person";
        readonly id: string;
        readonly name: string | null;
      }
    | { readonly kind: "team"; readonly name: string };
  readonly actions: readonly RequestAction[];
}

const SOMEBODY = "Somebody";

export function presentTeamRequest(
  queue: RequestQueue,
  row: TeamRequest,
): RequestPresentation {
  const subject = row.userDisplayName ?? SOMEBODY;
  const initiator = row.initiatedByDisplayName ?? SOMEBODY;

  if (queue === "mine") {
    return row.kind === "invitation"
      ? {
          direction: "inbound",
          headline: `${initiator} invited you to ${row.teamName}`,
          subject: {
            kind: "person",
            id: row.initiatedByUserId,
            name: row.initiatedByDisplayName,
          },
          actions: [
            { action: "accept", label: "Accept" },
            { action: "decline", label: "Decline", variant: "ghost" },
          ],
        }
      : {
          direction: "outbound",
          headline: `You asked to join ${row.teamName}`,
          subject: { kind: "team", name: row.teamName },
          actions: [{ action: "cancel", label: "Withdraw", variant: "ghost" }],
        };
  }

  return row.kind === "join_request"
    ? {
        direction: "inbound",
        headline: `${subject} asked to join ${row.teamName}`,
        subject: { kind: "person", id: row.userId, name: row.userDisplayName },
        actions: [
          { action: "accept", label: "Approve" },
          { action: "decline", label: "Decline", variant: "ghost" },
        ],
      }
    : {
        direction: "outbound",
        /* NOT "asked to join". This is the row the bug rendered backwards, and
           the sentence is the fix: the owner sent it, the invitee has not
           answered, and the only thing the owner can do is take it back. */
        headline: `You invited ${subject} to ${row.teamName} — waiting for them`,
        subject: { kind: "person", id: row.userId, name: row.userDisplayName },
        actions: [{ action: "cancel", label: "Withdraw", variant: "ghost" }],
      };
}
